import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { EmojiEvents } from '@mui/icons-material';
import Button from './Button';
import Card from './Card';
import { EmptyState, LoadingState } from './StateViews';
import { COLOR } from './tokens';
import { formatArcadeTimestamp, leaderboardEntryName, leaderboardEntryScore } from '../arcadeTheme';

function defaultScoreLabel(entry) {
    const s = leaderboardEntryScore(entry);
    return typeof s === 'number' && !Number.isInteger(s) ? s.toFixed(1) : String(s);
}

function entryKey(entry, idx) {
    return entry._id || entry.run_id || entry.id || entry.player_id || `entry-${idx}`;
}

/**
 * Ranked score list: rank, name, time, score chip and an optional per-row
 * action (e.g. the replay Watch button).
 */
export default function LeaderboardPanel({
    entries = [],
    loading = false,
    onRefresh,
    accentColor = COLOR.primary,
    formatScore,
    renderAction,
    emptyHint = 'No scores yet.',
    formatName = leaderboardEntryName,
    formatTime = (entry) => formatArcadeTimestamp(entry.timestamp || entry.last_played),
    limit = 12,
    title = 'Leaderboard',
}) {
    const label = typeof formatScore === 'function' ? formatScore : defaultScoreLabel;
    return (
        <Card
            data-testid="leaderboard-panel"
            title={title}
            icon={<EmojiEvents sx={{ color: COLOR.gold }} />}
            padded={false}
            action={onRefresh ? (
                <Button size="sm" variant="ghost" onClick={onRefresh} disabled={loading}>
                    {loading ? '…' : 'Refresh'}
                </Button>
            ) : null}
        >
            <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                {loading ? (
                    <LoadingState compact />
                ) : entries.length === 0 ? (
                    <EmptyState hint={emptyHint} icon={false} />
                ) : (
                    entries.slice(0, limit).map((entry, idx) => (
                        <Box
                            key={entryKey(entry, idx)}
                            sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: `1px solid ${COLOR.border}`,
                                '&:last-child': { borderBottom: 'none' },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                <Typography
                                    className="evx-tabular"
                                    sx={{ color: COLOR.textMuted, width: 20, fontWeight: 700, flexShrink: 0 }}
                                >
                                    {idx + 1}
                                </Typography>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: COLOR.text,
                                            fontWeight: 600,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {formatName(entry, idx)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: COLOR.textSubtle }}>
                                        {formatTime(entry)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                <Chip
                                    size="small"
                                    label={label(entry)}
                                    className="evx-tabular"
                                    sx={{
                                        bgcolor: 'transparent',
                                        border: `1px solid ${accentColor}`,
                                        color: accentColor,
                                        fontWeight: 700,
                                        height: 24,
                                    }}
                                />
                                {renderAction ? renderAction(entry) : null}
                            </Box>
                        </Box>
                    ))
                )}
            </Box>
        </Card>
    );
}
